/**
 * useReaderContext - Context and state management for canonical reader
 * Location: /app/frontend/src/components/page-builder/reader/hooks/
 * 
 * Phase 3-B: Enhanced with progress tracking, persistence, and improved mode switching
 */

import { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Valid modes
export const READER_MODES = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal',
  PAGE: 'page',
};

// Default mode
export const DEFAULT_MODE = READER_MODES.VERTICAL;

/**
 * Parse mode from query string
 * Supports both ?mode= and legacy ?reader= params
 */
export const parseModeFromQuery = (searchParams) => {
  const modeParam = searchParams.get('mode');
  if (modeParam && Object.values(READER_MODES).includes(modeParam.toLowerCase())) {
    return modeParam.toLowerCase();
  }
  
  const readerParam = searchParams.get('reader');
  if (readerParam && Object.values(READER_MODES).includes(readerParam.toLowerCase())) {
    return readerParam.toLowerCase();
  }
  
  return DEFAULT_MODE;
};

/**
 * Generate storage key for progress persistence
 */
const getProgressKey = (contentType, contentId, unitId, mode) => 
  `reader_progress:${contentType}:${contentId}:${unitId}:${mode}`;

/**
 * Load progress from localStorage
 */
export const loadProgress = (contentType, contentId, unitId, mode) => {
  try {
    const key = getProgressKey(contentType, contentId, unitId, mode);
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('[Reader] Failed to load progress:', e);
  }
  return null;
};

/**
 * Save progress to localStorage
 */
export const saveProgress = (contentType, contentId, unitId, mode, progressData) => {
  try {
    const key = getProgressKey(contentType, contentId, unitId, mode);
    const data = {
      ...progressData,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.warn('[Reader] Failed to save progress:', e);
  }
};

// Reader context
const ReaderContext = createContext(null);

/**
 * Reader context provider - Enhanced for Phase 3-B
 */
export const ReaderContextProvider = ({ 
  children,
  contentType,
  contentId,
  unitId,
  initialMode = DEFAULT_MODE,
  shellSettings = {},
}) => {
  const [mode, setModeState] = useState(initialMode);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [content, setContent] = useState(null);
  const [metadata, setMetadata] = useState(null);
  
  // Progress tracking
  const [progress, setProgressState] = useState({
    percent: 0,        // For vertical mode (0-100)
    index: 0,          // For page/horizontal mode
    total: 0,          // Total pages/sections
    scrollY: 0,        // Scroll position (vertical)
  });
  
  // Header visibility (for immersive mode)
  const [headerVisible, setHeaderVisible] = useState(true);
  
  // Device orientation
  const [isLandscape, setIsLandscape] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect device and orientation
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    window.addEventListener('orientationchange', checkDevice);
    
    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
    };
  }, []);

  // Mode switching with URL preservation
  const setMode = useCallback((newMode) => {
    if (Object.values(READER_MODES).includes(newMode)) {
      setModeState(newMode);
      setCurrentPage(0);
      
      // Update URL preserving other params
      const url = new URL(window.location.href);
      url.searchParams.set('mode', newMode);
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Progress update with optional persistence
  const setProgress = useCallback((progressData, persist = true) => {
    setProgressState(prev => {
      const newProgress = { ...prev, ...progressData };
      
      if (persist && contentType && contentId && unitId) {
        saveProgress(contentType, contentId, unitId, mode, newProgress);
      }
      
      return newProgress;
    });
  }, [contentType, contentId, unitId, mode]);

  // Load saved progress on mount or mode change
  useEffect(() => {
    if (contentType && contentId && unitId && !isLoading) {
      const saved = loadProgress(contentType, contentId, unitId, mode);
      if (saved) {
        setProgressState(prev => ({
          ...prev,
          ...saved,
        }));
        
        // Restore page index for page/horizontal modes
        if ((mode === READER_MODES.PAGE || mode === READER_MODES.HORIZONTAL) && saved.index !== undefined) {
          setCurrentPage(saved.index);
        }
      }
    }
  }, [contentType, contentId, unitId, mode, isLoading]);

  // Calculate total pages based on content
  const totalPages = content?.pages?.length || content?.paragraphs?.length || 0;

  // Page navigation
  const goToPage = useCallback((pageIndex) => {
    const max = totalPages - 1;
    if (pageIndex >= 0 && pageIndex <= max) {
      setCurrentPage(pageIndex);
      setProgress({ index: pageIndex, total: totalPages });
    }
  }, [totalPages, setProgress]);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      const newIndex = currentPage + 1;
      setCurrentPage(newIndex);
      setProgress({ index: newIndex, total: totalPages });
    }
  }, [currentPage, totalPages, setProgress]);

  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      const newIndex = currentPage - 1;
      setCurrentPage(newIndex);
      setProgress({ index: newIndex, total: totalPages });
    }
  }, [currentPage, totalPages, setProgress]);

  // Toggle header visibility
  const toggleHeader = useCallback(() => {
    setHeaderVisible(prev => !prev);
  }, []);

  const value = {
    // Static params
    contentType,
    contentId,
    unitId,
    // Mode
    mode,
    setMode,
    switchMode: setMode,
    // Content
    content,
    setContent,
    metadata,
    setMetadata,
    // Loading state
    isLoading,
    setIsLoading,
    error,
    setError,
    // Progress
    progress,
    setProgress,
    // Pagination
    currentPage,
    setCurrentPage,
    goToPage,
    nextPage,
    prevPage,
    totalPages,
    // UI State
    headerVisible,
    setHeaderVisible,
    toggleHeader,
    // Device info
    isMobile,
    isLandscape,
    // Page Builder settings (Phase 3-C)
    shellSettings,
    // Helpers
    loadProgress: () => loadProgress(contentType, contentId, unitId, mode),
    saveProgress: (data) => saveProgress(contentType, contentId, unitId, mode, data),
  };

  return (
    <ReaderContext.Provider value={value}>
      {children}
    </ReaderContext.Provider>
  );
};

/**
 * Hook to access reader context
 */
export const useReaderContext = () => {
  const context = useContext(ReaderContext);
  if (!context) {
    throw new Error('useReaderContext must be used within ReaderContextProvider');
  }
  return context;
};

export default useReaderContext;
